import { animations, previousTimeStamp } from "../animation";
import { addSideTrack, SIDE_LEFT, SIDE_RIGHT } from "../layout/overlay";
import { isHealthSummary } from "../layout/visibility";

const bubbleRad = 10;
const gapAng = 40;

// A callout is sized from its text, and the widest one on a side sets where every
// leader on that side docks. An unbounded summary would push that line across the
// map and squeeze every other leader, so cap it.
const maxSummaryChars = 56;

// Records that a scope ran, up the whole chain above it, so the filter panel can
// use "something actually went through here" as its measure of activity instead
// of log counts — a different question, and usually a better one.
//
// Two timestamps: one counting every scope, one counting everything that is not a
// health check. Health probes run against every service constantly, so with them
// included "has had a scope recently" is true of nearly the whole estate and the
// filter stops distinguishing anything. Keeping both means the toggle applies to
// what has already been seen, not just to what arrives next.
//
// Called again when the payload lands, because a scope's summary — and so whether
// it is a health check — arrives after the object itself.
function noteScopeActivity(item) {
  const at = Date.now();

  if (item.data && !item.summaryKnown) {
    try {
      item.isHealthScope = isHealthSummary(JSON.parse(item.data).summary);
      item.summaryKnown = true;
    } catch (e) {
      // Malformed payload; treat it as an ordinary scope.
    }
  }

  for (let a = item.parent; a; a = a.parent) {
    a.lastScopeAt = at;
    // Only once the summary has actually arrived. Stamping this on the way in
    // credited every scope as real work before anything knew what it was, so a
    // branch seeing nothing but health probes still counted as busy.
    if (item.summaryKnown && !item.isHealthScope) a.lastRealScopeAt = at;
  }
}

// The two wrappers the scheduler puts round a scope's name carry no information
// once you know what they mean, and between them they eat over half of the
// characters a callout has room for. An icon says the same thing in one column,
// which is the difference between reading the operation and reading the wrapper.
//
// Applied before the length cap, so the room they free goes to the name.
const SUMMARY_ICONS = [
  { pattern: /^Scheduled<(.*)>$/, icon: "⏲️" },
  { pattern: /^TriggerScheduleElapsedEvent:(.*)$/, icon: "⚡" },
];

function iconiseSummary(summary) {
  let text = summary;
  let icons = "";

  // Repeatedly, and in order, because the two nest: `Scheduled<Trigger…:X>`
  // unwraps to `⏲️` then `⚡` and comes out as `⏲️⚡X`.
  for (let again = true; again; ) {
    again = false;
    for (const { pattern, icon } of SUMMARY_ICONS) {
      const match = pattern.exec(text);
      if (!match) continue;
      icons += icon;
      text = match[1];
      again = true;
    }
  }

  return icons + text;
}

function summaryText(summary) {
  if (!summary) return summary;

  const text = iconiseSummary(summary);
  return text.length > maxSummaryChars
    ? text.slice(0, maxSummaryChars - 1) + "…"
    : text;
}
const gapX = Math.cos((gapAng * Math.PI) / 180);
const gapY = Math.sin((gapAng * Math.PI) / 180);

function setScopeProps(item, elemOverride) {
  if (item.scopePropSet || !item.data || (!item.elem && !elemOverride)) {
    // nothing
  } else {
    const info = JSON.parse(item.data);
    const scopeType = info.type;
    const scopeId = info.id.replaceAll("_", ":");
    (elemOverride || item.elem).attr(
      "data-props",
      JSON.stringify({
        [scopeType + " ID|scope.s\\:" + scopeType + "_str"]: scopeId,
      })
    );
    item.scopePropSet = true;

    if (info.detailJson) {
      const detail = JSON.parse(info.detailJson);
      if (detail && detail.forwards && detail.forwards.length) {
        item.ui.pingIp(detail.forwards[0], "scope");
      } else if (detail && detail.ip) {
        item.ui.pingIp(detail.ip, "scope");
      }
    }
  }
  // A scope past the track cap has no callout at all, and one that has not been
  // through its measurement phase has no element yet — either way there is
  // nowhere to put the outcome, and `outcomeEmojiSet` is never assigned, so this
  // is retried on every call until there is.
  if (item.outcomeEmojiSet || !item.outcomeEmoji) {
    // nothing
  } else if (item.track && item.track.overlayElement) {
    item.track.overlayElement.selectAll("text")[1].text(item.outcomeEmoji);
  }
}

export default {
  collections: { ["*"]: { objectType: "scope" } },

  layout: { radius: bubbleRad, unattached: true, stackSpacing: 5 },
  eventHandlers: {
    end: (item, eventData, ui) => {
      // 0 = failure, 1 = success, 2 = cancelled
      if (!item.track || !item.track.overlayElement) return;

      const outcome = JSON.parse(eventData).outcome;
      const res =
        outcome == 0 ? "✖" : outcome == 1 ? "✓" : outcome == 2 ? "■" : "❓";

      item.outcomeEmoji = res;
      setScopeProps(item);
    },
  },

  render: {
    enter: (item, ui) => {
      item.ui = ui;
      const { elem: e, id, data, label, parent } = item;
      let scopeDepth = 0;
      let p = parent;
      while (p.type == "scope") {
        scopeDepth++;
        p = p.parent;
      }

      noteScopeActivity(item);

      // make mouse-overable area, opacity 0. it's fiddly otherwise
      //e.append("circle").attr("r", bubbleRad).attr("opacity", 0);

      const bubble = e
        .append("path")
        .attr(
          "d",
          "M " +
            bubbleRad +
            ",0 A " +
            bubbleRad +
            "," +
            bubbleRad +
            " 0 1,0 " +
            gapX * bubbleRad +
            "," +
            gapY * bubbleRad
        )
        .attr("stroke", "#999")
        .attr("fill", "none")
        .attr("stroke-linecap", "round")
        .attr("stroke-width", 4);

      setScopeProps(item, bubble);

      function doPath(e, side) {
        const padding = 2;
        const c = e.select("circle");
        const texts = e.selectAll("text");
        const text = texts[0];
        const typeText = texts[1];

        // The whole callout mirrors, so this runs again whenever the layout moves
        // it to the other edge — which is why the anchoring lives here rather
        // than only at create time.
        text.attr("text-anchor", side == SIDE_LEFT ? "start" : "end");

        const textBox = text.getBBox();
        const ht = textBox.height;
        const wd = textBox.width + ht + padding;
        const path = e.select("path");
        const totW = wd + padding * 2 + (ht + padding);
        let ttx;
        if (side == SIDE_LEFT) {
          ttx = padding + ht / 2;
          text.attr("x", padding + ht + padding);
          path.attr(
            "d",
            "M 0 " +
              (ht * -0.5 - padding) +
              "H " +
              (wd + padding * 2) +
              "L " +
              totW +
              " 0" +
              "L " +
              (wd + padding * 2) +
              " " +
              (ht * 0.5 + padding) +
              "H 0" +
              "Z"
          );
        } else {
          ttx = -padding - ht / 2;
          text.attr("x", -padding - ht - padding);
          path.attr(
            "d",
            "M 0 " +
              (ht * -0.5 - padding) +
              "H " +
              -(wd + padding * 2) +
              "L " +
              -totW +
              " 0" +
              "L " +
              -(wd + padding * 2) +
              " " +
              (ht * 0.5 + padding) +
              "H 0" +
              "Z"
          );
        }

        typeText.attr("x", ttx);
        c.attr("cx", ttx).attr("r", ht / 2);
      }

      item.track = addSideTrack(
        bubble,
        item,
        (data, overlayHolder, side) => {
          data.gotData = !!data.data;
          const g = overlayHolder.append("g");
          g.append("path")
            .attr("stroke", "#999")
            .attr("fill", "white")
            .attr("stroke-width", 0.5);
          g.append("text")
            .attr("text-anchor", side == SIDE_LEFT ? "start" : "end")
            .attr("dominant-baseline", "middle")
            .attr("font-size", "0.7em")
            .text(
              summaryText(
                data.gotData ? JSON.parse(data.data).summary : data.label
              )
            );
          g.append("circle").attr("fill", "#555");
          g.append("text")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("font-size", "0.7em")
            .attr("font-weight", "bold")
            .attr("fill", "#fff")
            .text(data.gotData ? JSON.parse(data.data).type[0] : "?");
          return g;
        },
        (data, e, x, y, bounds, side, resized) => {
          setScopeProps(item);
          e.select("circle").attr(
            "fill",
            data.elem.select("path").attr("stroke")
          );
          if (data.gotData) return;
          if (!data.data) return;
          data.gotData = true;
          const d = JSON.parse(data.data);
          e.select("text").text(summaryText(d.summary));
          // doPath runs from the measurement phase that resized() schedules.
          resized();
        },
        () => {},
        // A starting guess only; the layout moves it to whichever side the scope
        // itself ends up on, and keeps it there through a dead-band. The parent
        // is used because a scope has no position of its own until the next
        // solve, and the parent's is already settled.
        item.parent.x < 0 ? SIDE_LEFT : SIDE_RIGHT,
        doPath
      );

      animations.add({
        type: "scope-hue",
        // Fifty minutes, against a scope that typically lives for seconds. The
        // item.gone check below ends it once the exit fade completes; the owner
        // covers every other way the element can leave the document.
        owner: bubble,
        start: previousTimeStamp,
        duration: 5000 * 60 * 10,
        from: 0,
        to: 1000 * 60 * 10,
        frame: (v, _, remove) => {
          if (item.gone) {
            remove();
            return;
          }
          const ang = (v / 1000) * 360 * 2;
          let hue;
          let sat = 75;
          if (v < 500) {
            hue = 180 - (v / 500) * 60;
          } else if (v < 750) {
            hue = 120;
          } else {
            hue = 120 - (v - 750) / 30;
            if (hue < 0) {
              hue = 0;
            }
          }
          if (v > 5000) {
            sat = 75 - (v - 5000) / 100;
            if (sat < 0) {
              sat = 0;
            }
          }
          bubble
            .attr("transform", `rotate(${ang - 90 - gapAng / 2})`)
            .attr("stroke", "hsl(" + hue + "," + sat + "%,50%)");
        },
      });
    },
    // The payload lands after the object does, and it is what says whether this
    // was a health check.
    update: (item) => noteScopeActivity(item),
    exit: (item) => {
      const e = item.elem;
      setScopeProps(item);
      animations.add({
        type: "scope-opacity",
        start: previousTimeStamp,
        duration: 1000,
        from: 1,
        to: 0,
        frame: (v, done) => {
          e.attr("opacity", v);
          if (done) {
            e.remove();
            item.gone = true;
          }
        },
      });
    },
  },
};
